"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import type { UIMessage } from "../types";
import { useChat } from "../hooks/useChat";
import { Tool } from "ai";

type ComposerContextType = {
  sendMessage: (message: string, additionalData?: Record<string, any>) => void;
  resetChat: () => void;
  messages: UIMessage[];
  status: "ready" | "streaming" | "submitted" | "error";
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: (
    event?: { target?: { value?: string } },
    options?: any,
  ) => void;
  stop: () => void;
  setMessages: (
    messages: UIMessage[] | ((prev: UIMessage[]) => UIMessage[]),
  ) => void;
  setAdditionalData: (data: Record<string, any> | undefined) => void;
  additionalData: Record<string, any> | undefined;
};

const defaultContext: ComposerContextType = {
  sendMessage: () => {},
  resetChat: () => {},
  messages: [],
  status: "ready",
  input: "",
  handleInputChange: () => {},
  handleSubmit: () => {},
  stop: () => {},
  setMessages: () => {},
  setAdditionalData: () => {},
  additionalData: undefined,
};

const ComposerContext = createContext<ComposerContextType>(defaultContext);

/**
 * A provider that manages chat state and functionality
 * This allows components throughout the app to use the useComposer hook
 */
export const ComposerProvider = ({
  children,
  orchestratorHost = "canvas",
  tools = {},
  initialAdditionalData,
}: {
  children: React.ReactNode;
  orchestratorHost?: string;
  tools?: Record<string, Tool>;
  initialAdditionalData?: Record<string, any>;
}) => {
  const [chatKey, setChatKey] = useState(Date.now());
  const [additionalData, setAdditionalData] = useState<
    Record<string, any> | undefined
  >(initialAdditionalData);

  // Update additionalData when initialAdditionalData changes
  useEffect(() => {
    if (initialAdditionalData) {
      setAdditionalData(initialAdditionalData);
    }
  }, [initialAdditionalData]);

  // Create a version of tools with only description and parameters for the API
  const toolDefinitions = Object.entries(tools).reduce(
    (acc, [name, tool]) => ({
      ...acc,
      [name]: {
        description: tool.description,
        parameters: tool.parameters,
      },
    }),
    {},
  );

  const {
    messages,
    input,
    handleSubmit: handleSubmitChat,
    handleInputChange,
    status,
    addToolResult,
    stop,
    setMessages,
  } = useChat({
    api: "/api/zsl/chat/completions",
    maxSteps: 10,
    id: `chat-${chatKey}`,
    onFinish(args, opts) {
      if (opts.finishReason === "length") {
        console.error("The response was too long.");
      }
    },
    async onToolCall({ toolCall }) {
      const toolName = toolCall.toolName;
      if (tools[toolName]) {
        const tool = tools[toolName];
        if (tool.execute) {
          try {
            const result = await tool.execute(toolCall.args, {
              toolCallId: toolCall.toolCallId,
              messages: [],
            });
            addToolResult({
              toolCallId: toolCall.toolCallId,
              result,
              options: {
                data: {
                  additionalData: additionalData || {},
                  tools: toolDefinitions,
                },
              },
            });
          } catch (error) {
            console.error(`Error executing tool ${toolName}:`, error);
            addToolResult({
              toolCallId: toolCall.toolCallId,
              result: {
                content: [
                  {
                    type: "text",
                    text: `Error executing tool ${toolName}: ${error}`,
                  },
                ],
              },
            });
          }
        }
      } else {
        console.error(`Tool not found: ${toolName}`);
        addToolResult({
          toolCallId: toolCall.toolCallId,
          result: {
            content: [
              {
                type: "text",
                text: `Tool not found: ${toolName}`,
              },
            ],
          },
          options: {
            data: {
              additionalData: additionalData || {},
              tools: toolDefinitions,
            },
          },
        });
      }
    },
    onError(error) {
      console.error("Error in chat:", error);
      // push an error message to the messages array
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "assistant",
          content: "An error occurred",
          id: "error-message",
          createdAt: new Date(),
          parts: [
            {
              type: "text",
              text: "Whoops, an error occurred in this chat. Please refresh this chat or start from scratch.",
            },
          ],
        },
      ]);
    },
  });

  const sendMessage = useCallback(
    (message: string, data?: Record<string, any>) => {
      // Set the input value to the message
      handleInputChange({
        target: { value: message },
      } as React.ChangeEvent<HTMLTextAreaElement>);

      // Submit after input change has taken effect
      handleSubmitChat(undefined, {
        data: {
          additionalData: data || additionalData || {},
          tools: toolDefinitions,
        },
        headers: {
          "x-orchestrator-host": orchestratorHost,
        },
      });
    },
    [
      handleInputChange,
      handleSubmitChat,
      orchestratorHost,
      additionalData,
      toolDefinitions,
    ],
  );

  const handleSubmit = useCallback(
    (event?: { target?: { value?: string } }, options?: any) => {
      handleSubmitChat(event, {
        data: {
          additionalData: options?.data || additionalData,
          tools: toolDefinitions,
        },
        headers: {
          "x-orchestrator-host": orchestratorHost,
          ...(options?.headers || {}),
        },
      });
    },
    [handleSubmitChat, orchestratorHost, additionalData, toolDefinitions],
  );

  const resetChat = useCallback(() => {
    stop();
    setChatKey(Date.now());
    setAdditionalData(initialAdditionalData);
  }, [initialAdditionalData, stop]);

  const value = {
    sendMessage,
    resetChat,
    messages,
    status,
    input,
    handleInputChange,
    handleSubmit,
    stop,
    setMessages,
    setAdditionalData,
    additionalData,
  };

  return (
    <ComposerContext.Provider value={value}>
      {children}
    </ComposerContext.Provider>
  );
};

export const useComposer = () => {
  const context = useContext(ComposerContext);
  if (!context) {
    throw new Error("useComposer must be used within a ComposerProvider");
  }
  return context;
};
